<script setup lang="ts">
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { Roles } from '@/constants.ts'
import pendingVerificationsHttpService, {
  type PendingVerification,
} from '@/services/http/pendingVerificationsHttpService.ts'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.Manager],
  },
})

const { t, locale } = useI18n()

const pendingQuery = useQuery({
  queryKey: computed(() => ['admin', 'pending-otp', locale.value]),
  queryFn: () => pendingVerificationsHttpService.getPending(),
})

const pending = computed<PendingVerification[]>(() => pendingQuery.data.value ?? [])

// Счётчик отвала по каналам — видно, какой канал бросают чаще (по проду email ~втрое чаще SMS).
const byChannel = computed(() => ({
  total: pending.value.length,
  sms: pending.value.filter((p) => p.channel === 'sms').length,
  email: pending.value.filter((p) => p.channel === 'email').length,
}))

function contactOf(p: PendingVerification): string {
  return p.email ?? p.phoneNumber ?? '—'
}

function channelLabel(p: PendingVerification): string {
  return p.channel === 'email' || p.channel === 'sms'
    ? t(`pendingOtp.channels.${p.channel}`)
    : p.channel
}

function formatRequestedAt(value: string | null): string {
  if (value == null) return '—'
  const date = new Date(value)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(date.getHours())}:${pad(date.getMinutes())} ${pad(date.getDate())}.${pad(date.getMonth() + 1)}`
}
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <div class="flex items-center justify-between w-full">
      <h1 class="text-xl font-bold">{{ t('pendingOtp.title') }}</h1>
    </div>

    <p class="text-sm text-surface-500 m-0" data-test="pending-summary">
      {{
        t('pendingOtp.summary', {
          total: byChannel.total,
          sms: byChannel.sms,
          email: byChannel.email,
        })
      }}
    </p>

    <DataTable
      :value="pending"
      :loading="pendingQuery.isLoading.value"
      data-key="id"
      class="w-full"
      :pt="{ bodyRow: { 'data-test': 'pending-row' } }"
    >
      <template #empty>
        <div class="text-sm text-surface-500 py-4">{{ t('pendingOtp.empty') }}</div>
      </template>

      <Column :header="t('pendingOtp.columns.contact')">
        <template #body="{ data }">
          <span data-test="pending-contact">{{ contactOf(data) }}</span>
        </template>
      </Column>
      <Column :header="t('pendingOtp.columns.channel')">
        <template #body="{ data }">{{ channelLabel(data) }}</template>
      </Column>
      <Column :header="t('pendingOtp.columns.requestedAt')">
        <template #body="{ data }">{{ formatRequestedAt(data.codeGenerationTime) }}</template>
      </Column>
      <Column field="sendCount" :header="t('pendingOtp.columns.sendCount')" />
      <Column :header="t('pendingOtp.columns.status')">
        <template #body="{ data }">
          <Tag v-if="data.locked" :value="t('pendingOtp.locked')" severity="danger" />
          <span v-else class="text-surface-400">—</span>
        </template>
      </Column>
    </DataTable>
  </section>
</template>
